import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
	TypographyComponent,
	TypographyVariant,
	TypographySize,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';
import { Alert, CounterPanel, CounterText } from './passenger.styles';
import { FlightPartyComposition } from '../../lib/utils/composition.service';
import CounterComponent from '../counter/counter.component';
import {
	IconFeedbackWarningComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';

enum Action {
	ADD = 'ADD',
	MINUS = 'MINUS'
}

export enum PassengerComposition {
	FULL = 'full',
	REDUCED = 'reduced'
}

export interface PassengerLabels {
	panel?: string;
	adults?: string;
	teens?: string;
	children?: string;
	infants?: string;
}

interface PassengerComponentProps {
	name: string;
	value: FlightPartyComposition;
	composition?: PassengerComposition;
	labels?: PassengerLabels;
}

const PassengerComponent = ({
	name,
	value,
	composition = PassengerComposition.FULL,
	labels = {
		panel: 'Passenger ages at time of travel',
		adults: 'Age 16 and over',
		teens: 'Age 12 to 15',
		children: 'Age 2 to 11',
		infants: 'Up to 2 years'
	}
}: PassengerComponentProps) => {
	const { setValue, getFieldState } = useFormContext();
	const [adults, setAdults] = useState<number>(value.adults || 1);
	const adultLowerLimit = 1;
	const adultUpperLimit = 9;
	const [teens, setTeens] = useState<number>(value.teens || 0);
	const teenLowerLimit = 0;
	const teenUpperLimit = 8;
	const [children, setChildren] = useState<number>(value.children || 0);
	const childLowerLimit = 0;
	const childUpperLimit = 8;
	const [infants, setInfants] = useState<number>(value.infants || 0);
	const infantLowerLimit = 0;
	const infantUpperLimit = 8;

	const updateAdults = (action: Action) => {
		if (action === Action.ADD) {
			if (adults === adultUpperLimit) return;
			setAdults(adults + 1);
		}

		if (action === Action.MINUS) {
			if (adults === adultLowerLimit) return;
			setAdults(adults - 1);
		}
	};

	const updateTeens = (action: Action) => {
		if (action === Action.ADD) {
			if (teens === teenUpperLimit) return;
			setTeens(teens + 1);
		}

		if (action === Action.MINUS) {
			if (teens === teenLowerLimit) return;
			setTeens(teens - 1);
		}
	};

	const updateChildren = (action: Action) => {
		if (action === Action.ADD) {
			if (children === childUpperLimit) return;
			setChildren(children + 1);
		}

		if (action === Action.MINUS) {
			if (children === childLowerLimit) return;
			setChildren(children - 1);
		}
	};

	const updateInfants = (action: Action) => {
		if (action === Action.ADD) {
			if (infants === infantUpperLimit) return;
			setInfants(infants + 1);
		}

		if (action === Action.MINUS) {
			if (infants === infantLowerLimit) return;
			setInfants(infants - 1);
		}
	};

	useEffect(() => {
		const comp: FlightPartyComposition = {
			adults: adults,
			teens: teens,
			children: children,
			infants: infants
		};

		setValue(name, comp, {
			shouldValidate: true,
			shouldDirty: true
		});
	}, [adults, teens, children, infants]);

	return (
		<div data-cy="passenger-component">
			{getFieldState(name)?.error && (
				<Alert data-testid="passenger-error">
					<IconFeedbackWarningComponent size={IconsSize.Sml} />
					{getFieldState(name)?.error?.message}
				</Alert>
			)}
			{labels.panel && (
				<TypographyComponent
					element="p"
					variant={TypographyVariant.Body}
					size={TypographySize.Sml}
					weight={TypographyWeight.Regular}>
					{labels.panel}
				</TypographyComponent>
			)}
			<CounterPanel noMargin={!labels.panel}>
				<CounterText data-testid="adult-label">
					<TypographyComponent
						element="p"
						variant={TypographyVariant.Body}
						size={TypographySize.Mdm}
						weight={TypographyWeight.Medium}>
						Adults
					</TypographyComponent>
					{labels.adults && (
						<TypographyComponent
							element="span"
							variant={TypographyVariant.Body}
							size={TypographySize.Xsm}
							weight={TypographyWeight.Regular}>
							{labels.adults}
						</TypographyComponent>
					)}
				</CounterText>
				<CounterComponent
					id="adult"
					minusDisabled={adults === adultLowerLimit}
					plusDisabled={adults === adultUpperLimit}
					minusOnChange={(): void => updateAdults(Action.MINUS)}
					plusOnChange={(): void => updateAdults(Action.ADD)}
					minusLabel="Remove adult"
					plusLabel="Add adult"
					value={adults}
				/>
			</CounterPanel>
			{composition === PassengerComposition.FULL && (
				<CounterPanel>
					<CounterText data-testid="teen-label">
						<TypographyComponent
							element="p"
							variant={TypographyVariant.Body}
							size={TypographySize.Mdm}
							weight={TypographyWeight.Medium}>
							Young adults
						</TypographyComponent>
						{labels.teens && (
							<TypographyComponent
								element="span"
								variant={TypographyVariant.Body}
								size={TypographySize.Xsm}
								weight={TypographyWeight.Regular}>
								{labels.teens}
							</TypographyComponent>
						)}
					</CounterText>
					<CounterComponent
						id="teen"
						minusDisabled={teens === teenLowerLimit}
						plusDisabled={teens === teenUpperLimit}
						minusOnChange={(): void => updateTeens(Action.MINUS)}
						plusOnChange={(): void => updateTeens(Action.ADD)}
						minusLabel="Remove teen"
						plusLabel="Add teen"
						value={teens}
					/>
				</CounterPanel>
			)}
			<CounterPanel>
				<CounterText data-testid="child-label">
					<TypographyComponent
						element="p"
						variant={TypographyVariant.Body}
						size={TypographySize.Mdm}
						weight={TypographyWeight.Medium}>
						Children
					</TypographyComponent>
					{labels.children && (
						<TypographyComponent
							element="span"
							variant={TypographyVariant.Body}
							size={TypographySize.Xsm}
							weight={TypographyWeight.Regular}>
							{labels.children}
						</TypographyComponent>
					)}
				</CounterText>
				<CounterComponent
					id="child"
					minusDisabled={children === childLowerLimit}
					plusDisabled={children === childUpperLimit}
					minusOnChange={(): void => updateChildren(Action.MINUS)}
					plusOnChange={(): void => updateChildren(Action.ADD)}
					minusLabel="Remove child"
					plusLabel="Add child"
					value={children}
				/>
			</CounterPanel>
			{composition === PassengerComposition.FULL && (
				<CounterPanel>
					<CounterText data-testid="infant-label">
						<TypographyComponent
							element="p"
							variant={TypographyVariant.Body}
							size={TypographySize.Mdm}
							weight={TypographyWeight.Medium}>
							Lap infant
						</TypographyComponent>
						{labels.infants && (
							<TypographyComponent
								element="span"
								variant={TypographyVariant.Body}
								size={TypographySize.Xsm}
								weight={TypographyWeight.Regular}>
								{labels.infants}
							</TypographyComponent>
						)}
					</CounterText>
					<CounterComponent
						id="infant"
						minusDisabled={infants === infantLowerLimit}
						plusDisabled={infants === infantUpperLimit}
						minusOnChange={(): void => updateInfants(Action.MINUS)}
						plusOnChange={(): void => updateInfants(Action.ADD)}
						minusLabel="Remove infant"
						plusLabel="Add infant"
						value={infants}
					/>
				</CounterPanel>
			)}
		</div>
	);
};

export default PassengerComponent;
